// Copyright 2022 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Link} from "react-router-dom";
import {Button, Modal, Table, Tag} from "antd";
import moment from "moment";
import * as Setting from "./Setting";
import * as PaymentBackend from "./backend/PaymentBackend";
import i18next from "i18next";
import BaseListPage from "./BaseListPage";
import * as Provider from "./auth/Provider";
import PopconfirmModal from "./common/modal/PopconfirmModal";

class PaymentListPage extends BaseListPage {

  componentDidMount() {
    super.componentDidMount();
    this.setState({
      callbackLoading: {},
      callbackModalVisible: false,
      callbackModalPayment: null,
      profitModalVisible: false,
      profitModalPayment: null,
      profitLoading: {},
      refundLoading: {},
    });
  }

  openProfitModal(payment) {
    this.setState({profitModalVisible: true, profitModalPayment: payment});
  }

  handleProfitModalCheckStatus() {
    const payment = this.state.profitModalPayment;
    if (!payment) {
      return;
    }
    this.setState({profitLoading: {...(this.state.profitLoading || {}), [payment.name || ""]: "checking"}});
    const paymentId = payment.owner + "/" + payment.name;
    PaymentBackend.getPaymentStatusFromWechat(paymentId).then((res) => {
      if (res.status === "ok") {
        this.setState({
          profitModalPayment: {...payment, wechatStatus: res.data},
          profitLoading: {...(this.state.profitLoading || {}), [payment.name || ""]: null},
        });
        Setting.showMessage("success", "查询成功");
      } else {
        Setting.showMessage("error", `查询失败: ${res.msg}`);
        this.setState({profitLoading: {...(this.state.profitLoading || {}), [payment.name || ""]: null}});
      }
    });
  }

  handleProfitModalConfirm() {
    const payment = this.state.profitModalPayment;
    this.setState({
      profitModalVisible: false,
      profitLoading: {...(this.state.profitLoading || {}), [payment.name || ""]: "doing"},
    });
    PaymentBackend.profitSharingFinish(payment).then((res) => {
      this.setState({profitLoading: {...(this.state.profitLoading || {}), [payment.name || ""]: null}});
      if (res.status === "ok") {
        Setting.showMessage("success", i18next.t("general:Successfully profit"));
        this.fetch({
          pagination: this.state.pagination || {current: 1, pageSize: 10},
          searchText: this.state.searchText || "",
          searchedColumn: this.state.searchedColumn || "",
        });
      } else {
        Setting.showMessage("error", `${i18next.t("general:Failed to profit")}: ${res.msg}`);
      }
    });
    this.setState({profitModalPayment: null});
  }

  handleProfitModalCancel() {
    this.setState({profitModalVisible: false, profitModalPayment: null});
  }

  openCallbackModal(payment) {
    this.setState({callbackModalVisible: true, callbackModalPayment: payment});
  }

  handleCallbackModalOk() {
    const payment = this.state.callbackModalPayment;
    this.setState({
      callbackModalVisible: false,
      callbackLoading: {...(this.state.callbackLoading || {}), [payment.name || ""]: true},
    });
    PaymentBackend.retryPaymentCallback(payment).then((res) => {
      if (res.status === "ok") {
        Setting.showMessage("success", i18next.t("general:Successfully triggered callback"));
        this.fetch({
          pagination: this.state.pagination || {current: 1, pageSize: 10},
          searchText: this.state.searchText || "",
          searchedColumn: this.state.searchedColumn || "",
        });
      } else {
        Setting.showMessage("error", `${i18next.t("general:Failed to trigger callback")}: ${res.msg}`);
      }
      this.setState({callbackLoading: {...(this.state.callbackLoading || {}), [payment.name || ""]: false}});
    });
    this.setState({callbackModalPayment: null});
  }

  handleCallbackModalClose() {
    const payment = this.state.callbackModalPayment;
    this.setState({
      callbackModalVisible: false,
      callbackLoading: {...(this.state.callbackLoading || {}), [payment.name || ""]: true},
    });
    PaymentBackend.closePaymentCallback(payment).then((res) => {
      if (res.status === "ok") {
        Setting.showMessage("success", "已关闭回调");
        this.fetch({
          pagination: this.state.pagination || {current: 1, pageSize: 10},
          searchText: this.state.searchText || "",
          searchedColumn: this.state.searchedColumn || "",
        });
      } else {
        Setting.showMessage("error", `关闭回调失败: ${res.msg}`);
      }
      this.setState({callbackLoading: {...(this.state.callbackLoading || {}), [payment.name || ""]: false}});
    });
    this.setState({callbackModalPayment: null});
  }

  handleCallbackModalCancel() {
    this.setState({callbackModalVisible: false, callbackModalPayment: null});
  }

  newPayment() {
    const randomName = Setting.getRandomName();
    const organizationName = Setting.getRequestOrganization(this.props.account);
    return {
      owner: organizationName,
      name: `payment_${randomName}`,
      createdTime: moment().format(),
      displayName: `New Payment - ${randomName}`,
      provider: "provider_pay_paypal",
      type: "PayPal",
      user: "admin",
      productName: "computer-1",
      productDisplayName: "A notebook computer",
      detail: "This is a computer with excellent CPU, memory and disk",
      tag: "Promotion-1",
      currency: "USD",
      price: 300.00,
      payUrl: "https://pay.com/pay.php",
      returnUrl: "https://door.casdoor.com/payments",
      state: "Paid",
      message: "",
    };
  }

  addPayment() {
    const newPayment = this.newPayment();
    PaymentBackend.addPayment(newPayment)
      .then((res) => {
        if (res.status === "ok") {
          this.props.history.push({pathname: `/payments/${newPayment.owner}/${newPayment.name}`, mode: "add"});
          Setting.showMessage("success", i18next.t("general:Successfully added"));
        } else {
          Setting.showMessage("error", `${i18next.t("general:Failed to add")}: ${res.msg}`);
        }
      }
      )
      .catch(error => {
        Setting.showMessage("error", `${i18next.t("general:Failed to connect to server")}: ${error}`);
      });
  }
  profitSharingFinish(index) {
    PaymentBackend.profitSharingFinish(this.state.data[index]).then((res) => {
      if (res.status === "ok") {
        Setting.showMessage("success", i18next.t("general:Successfully profit"));
        // this.handleTableChange();
      } else {
        Setting.showMessage("error", `${i18next.t("general:Failed to profit")}: ${res.msg}`);
      }
    });
  }
  refundPayment(index) {
    const payment = this.state.data[index];
    this.setState({refundLoading: {...this.state.refundLoading, [payment.name]: true}});
    PaymentBackend.refundPayment(payment).then((res) => {
      this.setState({refundLoading: {...this.state.refundLoading, [payment.name]: false}});
      if (res.status === "ok") {
        Setting.showMessage("success", i18next.t("general:Successfully refunded"));
        this.fetch({
          pagination: this.state.pagination || {current: 1, pageSize: 10},
          searchText: this.state.searchText || "",
          searchedColumn: this.state.searchedColumn || "",
        });
      } else {
        Setting.showMessage("error", `${i18next.t("general:Failed to refund")}: ${res.msg}`);
      }
    });
  }

  retryCallback(index) {
    const payment = this.state.data[index];
    this.setState({callbackLoading: {...this.state.callbackLoading, [payment.name]: true}});
    PaymentBackend.retryPaymentCallback(payment).then((res) => {
      this.setState({callbackLoading: {...this.state.callbackLoading, [payment.name]: false}});
      if (res.status === "ok") {
        Setting.showMessage("success", i18next.t("general:Successfully triggered callback"));
        this.handleTableChange();
      } else {
        Setting.showMessage("error", `${i18next.t("general:Failed to trigger callback")}: ${res.msg}`);
      }
    });
  }

  deletePayment(i) {
    PaymentBackend.deletePayment(this.state.data[i])
      .then((res) => {
        if (res.status === "ok") {
          Setting.showMessage("success", i18next.t("general:Successfully deleted"));
          this.setState({
            data: Setting.deleteRow(this.state.data, i),
            pagination: {total: this.state.pagination.total - 1},
          });
        } else {
          Setting.showMessage("error", `${i18next.t("general:Failed to delete")}: ${res.msg}`);
        }
      })
      .catch(error => {
        Setting.showMessage("error", `${i18next.t("general:Failed to connect to server")}: ${error}`);
      });
  }

  renderTable(payments) {
    const columns = [
      {
        title: i18next.t("general:Name"),
        dataIndex: "name",
        key: "name",
        width: "180px",
        fixed: "left",
        sorter: true,
        ...this.getColumnSearchProps("name"),
        render: (text, record, index) => {
          return (
            <Link to={`/payments/${record.owner}/${text}`}>
              {text}
            </Link>
          );
        },
      },
      {
        title: i18next.t("general:Organization"),
        dataIndex: "owner",
        key: "owner",
        width: "120px",
        sorter: true,
        ...this.getColumnSearchProps("owner"),
        render: (text, record, index) => {
          return (
            <Link to={`/organizations/${text}`}>
              {text}
            </Link>
          );
        },
      },
      {
        title: i18next.t("general:Provider"),
        dataIndex: "provider",
        key: "provider",
        width: "150px",
        fixed: "left",
        sorter: true,
        ...this.getColumnSearchProps("provider"),
        render: (text, record, index) => {
          return (
            <Link to={`/providers/${record.owner}/${text}`}>
              {text}
            </Link>
          );
        },
      },
      {
        title: i18next.t("general:User"),
        dataIndex: "user",
        key: "user",
        width: "120px",
        sorter: true,
        ...this.getColumnSearchProps("user"),
        render: (text, record, index) => {
          return (
            <Link to={`/users/${record.owner}/${text}`}>
              {text}
            </Link>
          );
        },
      },

      {
        title: i18next.t("general:Created time"),
        dataIndex: "createdTime",
        key: "createdTime",
        width: "160px",
        sorter: true,
        render: (text, record, index) => {
          return Setting.getFormattedDate(text);
        },
      },
      {
        title: i18next.t("provider:Type"),
        dataIndex: "type",
        key: "type",
        width: "140px",
        align: "center",
        filterMultiple: false,
        filters: Setting.getProviderTypeOptions("Payment").map((o) => {
          return {text: o.id, value: o.name};
        }),
        sorter: true,
        render: (text, record, index) => {
          record.category = "Payment";
          return Provider.getProviderLogoWidget(record);
        },
      },
      {
        title: i18next.t("payment:Product"),
        dataIndex: "productDisplayName",
        key: "productDisplayName",
        // width: '160px',
        sorter: true,
        ...this.getColumnSearchProps("productDisplayName"),
        render: (text, record, index) => {
          return (
            <Link to={`/products/${record.owner}/${record.productName}`}>
              {text}
            </Link>
          );
        },
      },
      {
        title: i18next.t("product:Price"),
        dataIndex: "price",
        key: "price",
        width: "120px",
        sorter: true,
        ...this.getColumnSearchProps("price"),
      },
      {
        title: i18next.t("payment:Currency"),
        dataIndex: "currency",
        key: "currency",
        width: "120px",
        sorter: true,
        ...this.getColumnSearchProps("currency"),
      },
      {
        title: i18next.t("general:State"),
        dataIndex: "state",
        key: "state",
        width: "120px",
        sorter: true,
        ...this.getColumnSearchProps("state"),
      },
      {
        title: "回调状态",
        dataIndex: "callbackState",
        key: "callbackState",
        width: "100px",
        render: (text, record, index) => {
          if (record.state !== "Paid") {
            return "-";
          }
          if (text === 1) {
            return <Tag color="green">成功</Tag>;
          } else if (text === -1) {
            return <Tag color="red">失败</Tag>;
          } else {
            return <Tag>未回调</Tag>;
          }
        },
      },
      {
        title: i18next.t("general:Action"),
        dataIndex: "",
        key: "op",
        width: "320px",
        fixed: (Setting.isMobile()) ? "false" : "right",
        render: (text, record, index) => {
          return (
            <div>
              <Button style={{marginTop: "10px", marginBottom: "10px", marginRight: "10px"}}
                onClick={() => this.props.history.push(`/payments/${record.owner}/${record.name}/result`)}>{i18next.t("payment:Result")}</Button>
              <Button style={{marginTop: "10px", marginBottom: "10px", marginRight: "10px"}} type="primary"
                onClick={() => this.props.history.push(`/payments/${record.owner}/${record.name}`)}>{i18next.t("general:Edit")}</Button>
              {record.state === "Paid" && (record.profitStatus === undefined || record.profitStatus !== 1) && (
                <Button style={{marginTop: "10px", marginBottom: "10px", marginRight: "10px", backgroundColor: "#52c41a", borderColor: "#52c41a", color: "#fff"}}
                  loading={(this.state.profitLoading || {})[record.name || ""] === "doing"}
                  onClick={() => this.openProfitModal(record)}
                >分账</Button>
              )}
              {record.state === "Paid" && record.profitStatus === 1 && (
                <Tag color="green" style={{marginTop: "10px", marginBottom: "10px", marginRight: "10px"}}>已分账</Tag>
              )}
              {record.state === "Paid" && (
                <Button style={{marginTop: "10px", marginBottom: "10px", marginRight: "10px", color: "#000"}}
                  loading={(this.state.refundLoading || {})[record.name || ""]}
                  onClick={() => {
                    if (window.confirm(`确定要退款 ${record.name} ?`)) {
                      this.refundPayment(index);
                    }
                  }}
                >退款</Button>
              )}
              {record.state === "Paid" && (
                <>
                  {record.callbackState !== 1 && record.callbackState !== undefined && (
                    <Button style={{marginTop: "10px", marginBottom: "10px", marginRight: "10px"}}
                      onClick={() => this.openCallbackModal(record)}
                    >回调</Button>
                  )}
                  {record.callbackState === 1 && (
                    <Tag color="green" style={{marginLeft: 8}}>已回调</Tag>
                  )}
                </>
              )}
              <PopconfirmModal
                title={i18next.t("general:Sure to delete") + `: ${record.name} ?`}
                onConfirm={() => this.deletePayment(index)}
              >
              </PopconfirmModal>
            </div>
          );
        },
      },
    ];

    const paginationProps = {
      total: this.state.pagination.total,
      showQuickJumper: true,
      showSizeChanger: true,
      showTotal: () => i18next.t("general:{total} in total").replace("{total}", this.state.pagination.total),
    };

    return (
      <div>
        <Table scroll={{x: "max-content"}} columns={columns} dataSource={payments}
          rowKey={(record) => `${record.owner}/${record.name}`} size="middle" bordered pagination={paginationProps}
          title={() => (
            <div>
              {i18next.t("general:Payments")}&nbsp;&nbsp;&nbsp;&nbsp;
              <Button type="primary" size="small"
                onClick={this.addPayment.bind(this)}>{i18next.t("general:Add")}</Button>
            </div>
          )}
          loading={this.state.loading}
          onChange={this.handleTableChange}
        />
        <Modal
          title={`订单回调 - ${this.state.callbackModalPayment?.name || ""}`}
          open={this.state.callbackModalVisible}
          onCancel={() => this.handleCallbackModalCancel()}
          footer={[
            <Button key="close" onClick={() => this.handleCallbackModalClose()}>
              关闭回调（不再触发）
            </Button>,
            <Button key="confirm" type="primary" onClick={() => this.handleCallbackModalOk()}>
              确认回调
            </Button>,
          ]}
        >
          <p>请选择操作：</p>
          <ul>
            <li><strong>确认回调</strong>：向业务后端发送支付成功通知</li>
            <li><strong>关闭回调</strong>：标记为已回调，不再触发（用于业务后台已手动处理的情况）</li>
          </ul>
        </Modal>
        <Modal
          title={`订单分账 - ${this.state.profitModalPayment?.name || ""}`}
          open={this.state.profitModalVisible}
          onCancel={() => this.handleProfitModalCancel()}
          footer={[
            <Button key="check" onClick={() => this.handleProfitModalCheckStatus()} loading={(this.state.profitLoading || {})[this.state.profitModalPayment?.name || ""] === "checking"}>
              查询微信支付状态
            </Button>,
            <Button key="cancel" onClick={() => this.handleProfitModalCancel()}>
              取消
            </Button>,
            <Button key="confirm" type="primary" onClick={() => this.handleProfitModalConfirm()} loading={(this.state.profitLoading || {})[this.state.profitModalPayment?.name || ""] === "doing"}>
              确认分账
            </Button>,
          ]}
        >
          {this.state.profitModalPayment && (
            <div>
              <p><strong>订单号：</strong>{this.state.profitModalPayment.name}</p>
              <p><strong>金额：</strong>¥{this.state.profitModalPayment.price?.toFixed(2)}</p>
              <p><strong>支付时间：</strong>{this.state.profitModalPayment.payTime || "-"}</p>
              {this.state.profitModalPayment.wechatStatus && (
                <div style={{marginTop: 16, padding: 12, background: "#f5f5f5", borderRadius: 4}}>
                  <p><strong>微信支付状态：</strong></p>
                  <p>交易状态：{this.state.profitModalPayment.wechatStatus.tradeStateMsg}</p>
                  {this.state.profitModalPayment.wechatStatus.successTime && (
                    <p>支付成功时间：{this.state.profitModalPayment.wechatStatus.successTime}</p>
                  )}
                  {this.state.profitModalPayment.wechatStatus.isRefunded && (
                    <p style={{color: "red"}}>⚠️ 已退款</p>
                  )}
                </div>
              )}
            </div>
          )}
        </Modal>
      </div>
    );
  }

  fetch = (params = {}) => {
    let field = params.searchedColumn, value = params.searchText;
    const sortField = params.sortField, sortOrder = params.sortOrder;
    if (params.type !== undefined && params.type !== null) {
      field = "type";
      value = params.type;
    }
    this.setState({loading: true});
    PaymentBackend.getPayments(Setting.isDefaultOrganizationSelected(this.props.account) ? "" : Setting.getRequestOrganization(this.props.account), params.pagination.current, params.pagination.pageSize, field, value, sortField, sortOrder)
      .then((res) => {
        this.setState({
          loading: false,
        });
        if (res.status === "ok") {
          this.setState({
            data: res.data,
            pagination: {
              ...params.pagination,
              total: res.data2,
            },
            searchText: params.searchText,
            searchedColumn: params.searchedColumn,
          });
        } else {
          if (Setting.isResponseDenied(res)) {
            this.setState({
              isAuthorized: false,
            });
          } else {
            Setting.showMessage("error", res.msg);
          }
        }
      });
  };
}

export default PaymentListPage;
